import { PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import type {
  StockRuleAlertLevel,
  StockRuleCalcType,
  StockRuleRow,
  StockRuleScopeType,
  StockRuleSaveRequest
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq, useAuthStore } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const SCOPE_META: Record<StockRuleScopeType, string> = {
  ORG: '本机构',
  ORG_WH: '指定仓库',
  GLOBAL: '全局默认'
};

const CALC_META: Record<StockRuleCalcType, string> = {
  FIXED: '固定值',
  DAYS_COVER: '按覆盖天数',
  AI: 'AI 测算'
};

const LEVEL_META: Record<StockRuleAlertLevel, { color: string; text: string }> = {
  INFO: { color: 'blue', text: '提示' },
  WARN: { color: 'orange', text: '警告' },
  CRITICAL: { color: 'red', text: '严重' }
};

interface RuleFormValues {
  scopeType: StockRuleScopeType;
  scopeIds?: string;
  materialId?: string;
  categoryId?: string;
  calcType: StockRuleCalcType;
  safetyStock: number;
  maxStock: number;
  emergencyStock: number;
  reorderPoint: number;
  reorderQty: number;
  coverDays: number;
  alertLevel: StockRuleAlertLevel;
  expiryWarnDays: number;
  idleDays: number;
  autoReplenish: boolean;
  remark?: string;
}

/** 规则对象描述：物资 → 品类 → 默认规则，与后端匹配优先级一致。 */
const targetText = (row: StockRuleRow): string => {
  if (row.materialId !== '0') {
    return [row.materialName, row.materialCode].filter(Boolean).join(' · ') || `未知物资 #${row.materialId}`;
  }
  if (row.categoryId !== '0') return `品类：${row.categoryName ?? row.categoryId}`;
  return '默认规则（全部物资）';
};

/**
 * 库存规则（`cfg_stock_rule`）：安全库存 / 补货点 / 补货批量的唯一配置入口，
 * 补货建议与库存预警都按「物资 → 品类 → 默认」逐级匹配这里的阈值。
 */
export default function StockRuleOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  // 与本端其它配置页同一口径：admin 放行，其余按权限码判定（只看 level 会漏掉 sys:config:manage）
  const isAdmin =
    profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'sys:config:manage') : false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StockRuleRow | null>(null);
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm<RuleFormValues>();

  const rules = useQuery({
    queryKey: ['ops', 'stock-rule', 'list'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.stockRule.list()
  });

  const categories = useQuery({
    queryKey: ['ops', 'category', 'options'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.categories({}),
    staleTime: 5 * 60 * 1000
  });

  const materials = useQuery({
    queryKey: ['ops', 'material', 'options', keyword],
    placeholderData: keepPreviousData,
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 }),
    enabled: open
  });

  const save = useMutation({
    mutationFn: (body: StockRuleSaveRequest) => hq.stockRule.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '规则已更新' : '规则已新增');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'stock-rule'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.stockRule.remove(id),
    onSuccess: async () => {
      message.success('规则已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'stock-rule'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      scopeType: 'ORG',
      calcType: 'FIXED',
      safetyStock: 0,
      maxStock: 0,
      emergencyStock: 0,
      reorderPoint: 0,
      reorderQty: 0,
      coverDays: 7,
      alertLevel: 'WARN',
      expiryWarnDays: 30,
      idleDays: 90,
      autoReplenish: true
    });
    setOpen(true);
  };

  const openEdit = (row: StockRuleRow) => {
    setEditing(row);
    form.setFieldsValue({
      scopeType: row.scopeType,
      scopeIds: row.scopeIds ?? undefined,
      materialId: row.materialId === '0' ? undefined : row.materialId,
      categoryId: row.categoryId === '0' ? undefined : row.categoryId,
      calcType: row.calcType,
      safetyStock: row.safetyStock,
      maxStock: row.maxStock,
      emergencyStock: row.emergencyStock,
      reorderPoint: row.reorderPoint,
      reorderQty: row.reorderQty,
      coverDays: row.coverDays,
      alertLevel: row.alertLevel,
      expiryWarnDays: row.expiryWarnDays,
      idleDays: row.idleDays,
      autoReplenish: row.autoReplenish,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const submit = (values: RuleFormValues) => {
    save.mutate({
      id: editing?.id,
      scopeType: values.scopeType,
      scopeIds: values.scopeIds || undefined,
      materialId: values.materialId ?? '0',
      categoryId: values.categoryId ?? '0',
      calcType: values.calcType,
      safetyStock: values.safetyStock,
      maxStock: values.maxStock,
      emergencyStock: values.emergencyStock,
      reorderPoint: values.reorderPoint,
      reorderQty: values.reorderQty,
      coverDays: values.coverDays,
      alertLevel: values.alertLevel,
      expiryWarnDays: values.expiryWarnDays,
      idleDays: values.idleDays,
      autoReplenish: values.autoReplenish,
      remark: values.remark
    });
  };

  const rows = rules.data ?? [];
  const error = rules.error instanceof ApiError ? rules.error.friendlyMessage : null;

  const columns: ColumnsType<StockRuleRow> = [
    {
      title: '适用范围',
      dataIndex: 'scopeType',
      width: 150,
      // 单行展示：仓库 ID 收进悬浮提示，避免行高被拉高
      render: (value: StockRuleScopeType, row) => (
        <Tooltip title={value === 'ORG_WH' ? `仓库 ${row.scopeIds ?? '-'}` : undefined}>
          <Tag color={value === 'GLOBAL' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>
            {SCOPE_META[value] ?? value}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: '规则对象',
      key: 'target',
      ellipsis: { showTitle: false },
      render: (_, row) => <TipText text={targetText(row)} />
    },
    { title: '计算方式', dataIndex: 'calcType', width: 120, render: (value: StockRuleCalcType) => CALC_META[value] ?? value },
    {
      title: '安全库存',
      dataIndex: 'safetyStock',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '最高库存',
      dataIndex: 'maxStock',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '应急储备',
      dataIndex: 'emergencyStock',
      width: 100,
      align: 'right',
      render: (value: number) => (value > 0 ? formatQty(value) : '-')
    },
    {
      title: '补货点',
      dataIndex: 'reorderPoint',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    {
      title: '补货批量',
      dataIndex: 'reorderQty',
      width: 100,
      align: 'right',
      render: (value: number) => formatQty(value)
    },
    { title: '覆盖天数', dataIndex: 'coverDays', width: 90, align: 'right' },
    { title: '近效期(天)', dataIndex: 'expiryWarnDays', width: 100, align: 'right' },
    {
      title: '预警级别',
      dataIndex: 'alertLevel',
      width: 100,
      render: (value: StockRuleAlertLevel) => {
        const meta = LEVEL_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '自动补货',
      dataIndex: 'autoReplenish',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="green">参与</Tag> : <Tag>不参与</Tag>)
    },
    {
      title: '更新时间',
      dataIndex: 'updateAt',
      width: 160,
      render: (value: string | null) => formatDisplayTime(value)
    },
    {
      title: '操作',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该规则？"
            description="删除后补货建议与库存预警将不再使用这些阈值"
            okText="删除"
            cancelText="取消"
            onConfirm={() => remove.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={remove.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          <SettingOutlined />
          库存规则
        </Space>
      }
      extra={
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={openCreate}>
            新建规则
          </Button>
          <Button icon={<ReloadOutlined />} loading={rules.isFetching} onClick={() => void rules.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="阈值口径"
        description="匹配优先级：指定物资 → 指定品类 → 默认规则（物资与品类均留空）。仅「自动补货=参与」的规则会进入补货建议扫描；目标库存取最高库存，未填则取安全库存/补货点的较大值。"
      />

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="加载失败" description={error} /> : null}

      <Table<StockRuleRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={rules.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无库存规则，点击「新建规则」配置安全库存' }}
      />

      <Modal
        title={editing ? '编辑库存规则' : '新建库存规则'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        width={680}
        destroyOnHidden
      >
        <Form<RuleFormValues> form={form} layout="vertical" onFinish={submit}>
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="scopeType" label="适用范围" rules={[{ required: true, message: '请选择适用范围' }]} style={{ width: 200 }}>
              <Select
                options={[
                  { value: 'ORG', label: '本机构' },
                  { value: 'ORG_WH', label: '指定仓库' },
                  ...(isAdmin ? [{ value: 'GLOBAL', label: '全局默认（管理员）' }] : [])
                ]}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.scopeType !== next.scopeType}
            >
              {({ getFieldValue }) =>
                getFieldValue('scopeType') === 'ORG_WH' ? (
                  <Form.Item name="scopeIds" label="仓库 ID" rules={[{ required: true, message: '请填写仓库 ID' }]}>
                    <Input style={{ width: 220 }} placeholder="多个用逗号分隔" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="materialId"
              label="指定物资"
              dependencies={['categoryId']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || !getFieldValue('categoryId')) return Promise.resolve();
                    return Promise.reject(new Error('物资与品类只能指定其一'));
                  }
                })
              ]}
              style={{ width: 320 }}
            >
              <Select
                allowClear
                showSearch
                filterOption={false}
                loading={materials.isFetching}
                placeholder="留空=不限物资"
                onSearch={setKeyword}
                options={(materials.data?.items ?? []).map((item) => ({
                  value: item.id,
                  label: [item.materialName, item.materialCode].filter(Boolean).join(' · ')
                }))}
              />
            </Form.Item>
            <Form.Item name="categoryId" label="或按品类" style={{ width: 260 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="留空=不限品类"
                options={(categories.data ?? []).map((item) => ({
                  value: item.id,
                  label: item.categoryName
                }))}
              />
            </Form.Item>
            <Form.Item name="calcType" label="计算方式" style={{ width: 160 }}>
              <Select
                options={[
                  { value: 'FIXED', label: '固定值' },
                  { value: 'DAYS_COVER', label: '按覆盖天数' },
                  { value: 'AI', label: 'AI 测算' }
                ]}
              />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="safetyStock" label="安全库存" style={{ width: 150 }}>
              <InputNumber min={0} precision={3} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxStock" label="最高库存" style={{ width: 150 }}>
              <InputNumber min={0} precision={3} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="emergencyStock"
              label="应急储备量"
              tooltip="可用库存低于该值时单独产生「应急储备不足」预警；0 = 不启用"
              style={{ width: 150 }}
            >
              <InputNumber min={0} precision={3} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reorderPoint" label="补货点" style={{ width: 150 }}>
              <InputNumber min={0} precision={3} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reorderQty" label="补货批量" style={{ width: 150 }}>
              <InputNumber min={0} precision={3} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="coverDays" label="覆盖天数" style={{ width: 130 }}>
              <InputNumber min={0} max={365} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="expiryWarnDays" label="近效期预警天数" style={{ width: 150 }}>
              <InputNumber min={1} max={365} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="idleDays" label="呆滞天数阈值" style={{ width: 150 }}>
              <InputNumber min={1} max={3650} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="alertLevel" label="预警级别" style={{ width: 140 }}>
              <Select
                options={[
                  { value: 'INFO', label: '提示' },
                  { value: 'WARN', label: '警告' },
                  { value: 'CRITICAL', label: '严重' }
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item
            name="autoReplenish"
            label="参与自动补货建议"
            valuePropName="checked"
            extra="关闭后该规则只用于库存预警，不生成补货建议"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
